import React, { useEffect, useState } from 'react'
import {
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material'
import { HttpClient } from '../../../api/httpClient'
import { API_URL } from '../../../api/RestAPI'
import apiHeaders from '../../../api/apiHeaders'
import SectionSpacingFields from './shared/SectionSpacingFields'
import useTranslation from '../../../hooks/useTranslation'
import toast from '../../../utils/toast'

export default function CollectionSettings({ settings, onChange }) {
  const { t } = useTranslation()
  const [categories, setCategories] = useState([])
  const [collections, setCollections] = useState([])

  useEffect(() => {
    HttpClient.get(`${API_URL}/categories?limit=all`, apiHeaders())
      .then((res) => {
        const cats = res.data?.data || res.data?.categories || []
        setCategories(Array.isArray(cats) ? cats : [])
      })
      .catch(() => toast.error(t('labels.themes.failedToLoadCategories')))

    HttpClient.get(`${API_URL}/collections?limit=all`, apiHeaders())
      .then((res) => {
        const cols = res.data?.data || res.data?.collections || []
        setCollections(Array.isArray(cols) ? cols : [])
      })
      .catch(() => toast.error(t('labels.themes.failedToLoadCollections')))
  }, [])

  const sourceType = settings.sourceType || 'category'
  const sources = sourceType === 'collection' ? collections : categories
  const columns = settings.columns ?? 2
  const rows = settings.rows ?? 2

  return (
    <Stack spacing={2}>
      <SectionSpacingFields
        settings={settings}
        onChange={onChange}
      />

      <FormControl size="small" fullWidth>
        <InputLabel>{t('labels.themes.sourceType')}</InputLabel>
        <Select
          value={sourceType}
          label={t('labels.themes.sourceType')}
          onChange={(e) => onChange({
            ...settings,
            sourceType: e.target.value,
            sourceId: '',
            sourceName: '',
            sourceSlug: '',
            title: ''
          })}
        >
          <MenuItem value="category">{t('labels.themes.category')}</MenuItem>
          <MenuItem value="collection">{t('labels.themes.collection')}</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel>{sourceType === 'collection' ? t('labels.themes.collection') : t('labels.themes.category')}</InputLabel>
        <Select
          value={settings.sourceId || ''}
          label={sourceType === 'collection' ? t('labels.themes.collection') : t('labels.themes.category')}
          onChange={(e) => {
            const selected = sources.find((s) => String(s.id) === String(e.target.value))

            onChange({
              ...settings,
              sourceId: e.target.value,
              sourceName: selected?.name || '',
              sourceSlug: selected?.slug || '',
              title: settings.title !== undefined && settings.title !== '' ? settings.title : (selected?.name || '')
            })
          }}
        >
          {sources.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label={t('labels.themes.sectionTitle')}
        size="small"
        fullWidth
        value={settings.title !== undefined ? settings.title : (settings.sourceName || '')}
        onChange={(e) => onChange({ ...settings, title: e.target.value })}
        helperText={t('labels.themes.sectionTitleHelper')}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          label={t('labels.themes.columns')}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 1, max: 4 }}
          value={columns}
          onChange={(e) => onChange({
            ...settings,
            columns: parseInt(e.target.value, 10) || 2
          })}
        />

        <TextField
          label={t('labels.themes.rows')}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 1, max: 4 }}
          value={rows}
          onChange={(e) => onChange({
            ...settings,
            rows: parseInt(e.target.value, 10) || 2
          })}
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <TextField
          label={t('labels.themes.productsToShow')}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 1, max: 40 }}
          value={settings.productsToShow ?? (columns * rows)}
          onChange={(e) => onChange({
            ...settings,
            productsToShow: parseInt(e.target.value, 10) || (columns * rows)
          })}
        />

        <TextField
          label={t('labels.themes.cardSpacing')}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 60 }}
          value={settings.gap ?? 16}
          onChange={(e) => onChange({
            ...settings,
            gap: parseInt(e.target.value, 10) || 0
          })}
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={settings.showMoreLink !== false}
            onChange={(e) => onChange({
              ...settings,
              showMoreLink: e.target.checked
            })}
          />
        }
        label={t('labels.themes.showViewAllLink')}
      />
    </Stack>
  )
}